// ─── LocalActivityStorage — offline-first persistence for walks ─────────────────
import type { ActivityStats, GpsPoint, LocalActivity } from "./types";

const STORAGE_KEY = "walk_activities_local";

export const LocalActivityStorage = {
  save(activity: LocalActivity): void {
    try {
      const list = this.getAll();
      const idx = list.findIndex(a => a.id === activity.id);
      if (idx >= 0) list[idx] = activity;
      else list.unshift(activity);
      // Keep last 50 local activities to avoid storage overflow
      const trimmed = list.slice(0, 50);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    } catch {}
  },

  getAll(): LocalActivity[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as LocalActivity[]) : [];
    } catch { return []; }
  },

  getUnsynced(): LocalActivity[] {
    return this.getAll().filter(a => !a.synced);
  },

  markSynced(id: string): void {
    try {
      const list = this.getAll();
      const idx = list.findIndex(a => a.id === id);
      if (idx >= 0) {
        list[idx].synced = true;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      }
    } catch {}
  },

  isSynced(id: string): boolean {
    const list = this.getAll();
    const item = list.find(a => a.id === id);
    return item?.synced === true;
  },

  delete(id: string): void {
    try {
      const list = this.getAll().filter(a => a.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {}
  },

  clearAll(): void {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  },
};

export function createLocalActivity(stats: ActivityStats, points: GpsPoint[]): LocalActivity {
  return {
    id: crypto.randomUUID(),
    startedAt: points.length > 0 ? points[0].ts : Date.now(),
    endedAt: Date.now(),
    stats,
    points,
    synced: false,
  };
}
